// Module 5 / Slice 5 — Rota Configuration detail screen.
//
// Header card with rota meta + 3 tabs (Pattern, Positions, Assignments)
// loaded via the composite GET /rotas/{id}/timeline endpoint.
//
// Per MODULE_5_PLAN.md §10 the SPA stays behind the
// `rota.admin_ui.enabled` feature flag at the AppShell nav level.
//
// PATTERNS.md §2.2 (screen composition) + §2.5 (data-testid naming).

import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { rotaConfigApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";
import { PatternTab } from "./tabs/PatternTab";
import { PositionsTab } from "./tabs/PositionsTab";
import { AssignmentsTab } from "./tabs/AssignmentsTab";

type TabKey = "pattern" | "positions" | "assignments";

export function RotaDetailScreen(): JSX.Element {
  const { t } = useI18n();
  const { rotaId } = useParams({ from: "/authed/admin/rota-config/rotas/$rotaId" });
  const [tab, setTab] = useState<TabKey>("pattern");

  const query = useQuery({
    queryKey: ["rota-config", "rota-timeline", rotaId] as const,
    queryFn: async () => {
      const response = await rotaConfigApiClient.GET(
        "/rota-config/rotas/{rotaId}/timeline",
        { params: { path: { rotaId } } },
      );
      if (!response.data) {
        throw new Error("rota_timeline_fetch_failed");
      }
      return response.data;
    },
  });

  if (query.isLoading) {
    return <p data-testid="rota-detail-loading">{t("rota_config.rotas.loading")}</p>;
  }
  if (query.isError || !query.data) {
    return (
      <p
        role="alert"
        data-testid="rota-detail-error"
        style={{ color: "var(--rc-color-danger)" }}
      >
        {(query.error as Error | undefined)?.message ?? "Rota not found"}
      </p>
    );
  }
  const { rota, patterns, positions, assignments } = query.data;

  return (
    <div data-testid="rota-detail-screen">
      <header className="rc-detail-header">
        <h1 data-testid="rota-detail-name">{rota.name}</h1>
        <p className="rc-help">{rota.description ?? t("rota_config.rota.no_description")}</p>
        <div data-testid="rota-detail-meta">
          <span data-testid="rota-detail-status" className={`rc-status-badge rc-status-${rota.status}`}>
            {rota.status}
          </span>
          <span data-testid="rota-detail-org-unit">{rota.orgUnitId}</span>
          {rota.effectiveFrom && (
            <span data-testid="rota-detail-effective-from">{rota.effectiveFrom}</span>
          )}
          {rota.effectiveTo && (
            <span data-testid="rota-detail-effective-to">{rota.effectiveTo}</span>
          )}
        </div>
      </header>

      <nav className="rc-tabs" role="tablist" data-testid="rota-detail-tabs">
        {(["pattern", "positions", "assignments"] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            data-testid={`rota-detail-tab-${k}`}
            onClick={() => setTab(k)}
          >
            {t(`rota_config.rotas.tab.${k}`)}
          </button>
        ))}
      </nav>

      <section role="tabpanel" data-testid={`rota-detail-panel-${tab}`}>
        {tab === "pattern" && <PatternTab rotaId={rota.id} patterns={patterns} />}
        {tab === "positions" && <PositionsTab rotaId={rota.id} positions={positions} />}
        {tab === "assignments" && (
          <AssignmentsTab rotaId={rota.id} assignments={assignments} />
        )}
      </section>
    </div>
  );
}
